<template>
  <Modal
    :modal-style="{ width: '500px' }"
    :title="title"
  >
    <QSpinner
      v-if="loading"
      class="absolute-center"
      size="50"
    />
    <ShareWithUsersSelector
      v-model="localSharedWithUsers"
      :access-levels="accessLevels"
      class="q-mt-lg"
      :organisation-teams="organisationTeams"
      :users="shareableUsers"
    >
      <template #afterTeamSelect>
        <span class="soft field-subtext q-mt-md q-pt-xs">
          Users with edit privileges can edit, move or remove themselves from
          the shared {{ labelForStreamType(streamType) }}.
          <br />
          Users without edit privileges can only move or remove themselves from
          the shared {{ labelForStreamType(streamType) }}.
        </span>
      </template>
    </ShareWithUsersSelector>

    <template #buttons="{ close }">
      <QBtn
        class="q-mr-sm"
        label="Cancel"
        @click.prevent="close"
      />

      <QBtn
        color="primary"
        label="Apply Changes"
        @click="confirm"
      />
    </template>
  </Modal>
</template>

<script>
import { compact, keyBy, sortBy, uniq } from "lodash-es";

import Modal from "shared/components/core/modals/Modal.vue";
import ShareWithUsersSelector from "shared/components/selectors/ShareWithUsersSelector.vue";
import { getAccessLevels, streamTypes } from "shared/constants";
import { labelForStreamType } from "shared/helpers/stream";
import { Stream } from "shared/resources";
import StreamsUser from "shared/resources/StreamsUser";

export default {
  name: "ManageStreamsUsersModal",
  components: {
    Modal,
    ShareWithUsersSelector,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
    users: {
      type: Array,
      required: true,
    },
    streamId: {
      type: [Number, String],
      default: "",
    },
    streamIds: {
      type: Array,
      default: () => [],
    },
    streamType: {
      type: Number,
      required: true,
      validator: (value) => Object.values(streamTypes).includes(value),
    },
  },
  emits: ["confirm"],
  data() {
    return {
      loading: true,
      localSharedWithUsers: [],
      accessLevels: getAccessLevels(),
      streamsUsers: [],
    };
  },
  computed: {
    isBulkUpdate() {
      return this.streamIds?.length;
    },
    streamIdTitle() {
      return this.streamId || this.streamIds.join();
    },
    title() {
      return `Apply Users to Stream ${this.streamIdTitle}`;
    },
    shareableUsers() {
      return sortBy(
        this.users.filter(({ id }) => id !== Number(this.streamOwnerId)),
        "name"
      );
    },
    streamOwnerId() {
      if (this.isBulkUpdate) return null;

      return this.streamsUsers.find(
        ({ accessLevel }) => accessLevel === "owner"
      )?.userId;
    },
    organisationTeams() {
      const teams = uniq(
        this.users.reduce(
          (list, user) => [
            ...list,
            ...[user.teamName],
            ...user.secondaryTeamNames,
          ],
          []
        )
      );

      return compact(teams).map((team) => ({ name: team }));
    },
  },
  mounted() {
    this.loadStream();
  },
  methods: {
    labelForStreamType,
    async loadStream() {
      try {
        const response = await Stream.includes([
          "user",
          { streamsUsers: ["user"] },
        ])
          .where({
            id: this.streamId || this.streamIds,
            organisationId: this.organisationId,
            archived: false,
          })
          .all();

        this.streamsUsers = response.data.flatMap(
          (stream) => stream.streamsUsers
        );

        this.localSharedWithUsers = this.streamsUsers
          .filter(
            (streamUser) =>
              streamUser.accessLevel !== "owner" &&
              (!streamUser.archivedAt || streamUser.archived === false)
          )
          .map((streamUser) => ({
            id: streamUser.userId,
            accessLevel:
              streamUser.accessLevel ||
              (streamUser.canEdit ? "read_write" : "read_only"),
          }));

        // Display the checkmark in the bulk share modal only when all selected streams have been shared with a user
        if (!this.streamId && this.streamIds) {
          this.localSharedWithUsers = this.localSharedWithUsers.filter(
            this.isAllSelectedStreamsSharedWithUser
          );
        }
      } catch (error) {
        this.$error(
          `Unable to load mention stream id ${this.streamIdTitle}`,
          error
        );
      } finally {
        this.loading = false;
      }
    },
    isAllSelectedStreamsSharedWithUser(user) {
      return (
        this.streamIds.length ===
        this.localSharedWithUsers.filter(
          (currentUser) => currentUser.id === user.id
        ).length
      );
    },
    buildUpdatedUserAccess(existingRecord, accessLevel) {
      const updatedRecord = existingRecord.dupWithDirtyTracking();

      if (accessLevel) {
        updatedRecord.archived = false;
        updatedRecord.accessLevel = accessLevel;
      } else if (!updatedRecord.archived) updatedRecord.archived = true;

      return updatedRecord;
    },
    updateShares(sharedWith) {
      const lookup = keyBy(this.streamsUsers, "userId");

      const updatedShares = this.streamOwnerId
        ? [lookup[this.streamOwnerId]]
        : [];

      sharedWith.forEach((user) => {
        const existingRecord = lookup[user.id];

        if (existingRecord && !this.isBulkUpdate) {
          const updatedRecord = this.buildUpdatedUserAccess(
            existingRecord,
            user.accessLevel
          );

          updatedShares.push(updatedRecord);
        } else if (user.accessLevel) {
          updatedShares.push(
            new StreamsUser({
              userId: user.id,
              accessLevel: user.accessLevel,
            })
          );
        }
      });

      return updatedShares;
    },
    confirm() {
      const updatedShares = this.updateShares(this.localSharedWithUsers);
      this.$emit("confirm", updatedShares);
    },
  },
};
</script>
